/** @jsx jsx */
import { jsx } from '@emotion/core';
import PropTypes from 'prop-types';

import { Button, Heading, Checkbox, withTheme, Loading } from 'school-space-client/components';
import { getMonthYearStringFromDate } from 'school-space-utils/date';

const css = {
    grid: ({ loading }) => theme =>
        loading
            ? {
                  height: theme.layout.baseline * 11
              }
            : {
                  ...theme.styles.grid({ gap: theme.layout.baseline / 2, numColumns: 2 }),
                  [theme.helpers.mq('s')]: {
                      ...theme.styles.grid({ gap: theme.layout.baseline / 2, numColumns: 3 })
                  },
                  [theme.helpers.mq('l')]: {
                      ...theme.styles.grid({ gap: theme.layout.baseline, numColumns: 4 })
                  }
              },
    header: theme => ({
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.layout.baseline,
        flexWrap: 'wrap'
    }),
    buttons: theme => ({
        display: 'flex',
        width: '100%',
        [theme.helpers.mq('s')]: {
            width: 'auto',
            marginBottom: 0
        }
    }),
    button: theme => ({
        marginLeft: theme.layout.baseline,
        '&:first-of-type': {
            marginLeft: 0
        }
    }),
    key: theme => ({
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.layout.baseline / 2,
        [theme.helpers.mq('s')]: {
            marginBottom: 0
        }
    }),
    keyField: theme => ({
        marginRight: theme.layout.baseline,
        padding: theme.layout.baseline / 4,
        '&:last-of-type': {
            marginRight: 0
        }
    }),
    backgroundHighlight: ({ isBankHoliday, isSchoolHoliday, isNonOperational }) => theme => {
        if (isNonOperational) {
            return { backgroundColor: theme.colors.greyLighter };
        }

        if (isBankHoliday) {
            return { backgroundColor: theme.colors.warningLight };
        }

        if (isSchoolHoliday) {
            return { backgroundColor: theme.colors.primaryLighter };
        }

        return {};
    },
    monthLabel: {
        marginTop: 0
    },
    dateLabel: theme => ({
        padding: theme.layout.baseline / 8
    })
};

const groupDatesByMonth = dates =>
    dates.reduce((groups, date) => {
        const month = getMonthYearStringFromDate(date.date);
        const existingGroupIndex = groups.findIndex(({ label }) => label === month);

        let newGroups = [...groups];

        if (existingGroupIndex !== -1) {
            const { label, datesForMonth } = groups[existingGroupIndex];

            newGroups[existingGroupIndex] = {
                label,
                datesForMonth: [...datesForMonth, date]
            };
        } else {
            newGroups = [
                ...newGroups,
                {
                    label: month,
                    datesForMonth: [date]
                }
            ];
        }

        return newGroups;
    }, []);

const DateSelector = ({ dates, toggleDate, deselectHolidays, selectAll, loading }) => {
    const datesByMonth = groupDatesByMonth(dates);

    return (
        <div css={css.container}>
            <div css={css.header}>
                <div css={css.key}>
                    <span
                        css={withTheme([
                            css.keyField,
                            css.backgroundHighlight({ isSchoolHoliday: true })
                        ])}
                    >
                        School holidays
                    </span>
                    <span
                        css={withTheme([
                            css.keyField,
                            css.backgroundHighlight({ isBankHoliday: true })
                        ])}
                    >
                        Bank holidays
                    </span>
                    <span
                        css={withTheme([
                            css.keyField,
                            css.backgroundHighlight({ isNonOperational: true })
                        ])}
                    >
                        School closed
                    </span>
                </div>
                <div css={css.buttons}>
                    <Button state="text" size="small" onClick={selectAll} styles={css.button}>
                        Select all
                    </Button>
                    <Button
                        state="text"
                        size="small"
                        onClick={deselectHolidays}
                        styles={css.button}
                    >
                        Remove school holidays
                    </Button>
                </div>
            </div>
            <div css={css.grid({ loading })}>
                {loading ? (
                    <Loading fullHeight />
                ) : (
                    datesByMonth.map(({ label, datesForMonth }) => {
                        return (
                            <div key={label}>
                                <Heading level="3" styles={css.monthLabel}>
                                    {label}
                                </Heading>
                                {datesForMonth.map(
                                    ({
                                        selected,
                                        isBankHoliday,
                                        isSchoolHoliday,
                                        isNonOperational,
                                        dateString,
                                        date
                                    }) => {
                                        return (
                                            <Checkbox
                                                key={date}
                                                checked={selected}
                                                name={date}
                                                label={dateString}
                                                inlineLabel
                                                onChange={() => toggleDate(date)}
                                                disabled={isNonOperational}
                                                labelStyles={withTheme([
                                                    css.dateLabel,
                                                    css.backgroundHighlight({
                                                        isBankHoliday,
                                                        isSchoolHoliday,
                                                        isNonOperational
                                                    })
                                                ])}
                                            />
                                        );
                                    }
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

DateSelector.propTypes = {
    dates: PropTypes.arrayOf(PropTypes.object).isRequired,
    toggleDate: PropTypes.func.isRequired,
    deselectHolidays: PropTypes.func.isRequired,
    selectAll: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired
};

export default DateSelector;
